/**
 * TreeManager - hierarchical structure editor with drag & drop.
 *
 * Level convention: the root is level 1. levelNames, the level-N CSS classes
 * and the timeline (which reads level 1/2/3 as group / sub-group / task) are
 * all 1-based. level and parentId are re-derived from the real nesting by
 * _reindex() after every change, so nothing has to keep them in sync by hand
 * and data loaded from the server (which sends parent_id, not parentId) is
 * healed on the way in.
 */
class TreeManager {
  constructor(config = {}) {
    this.containerId = config.containerId || 'tree';
    this.container = config.container || document.getElementById(this.containerId);
    this.limitedDepth = config.limitedDepth !== undefined ? config.limitedDepth : true;
    this.maxDepth = config.maxDepth || 3;
    this.levelNames = config.levelNames || {
      1: 'Stage',
      2: 'Area',
      3: 'Zone'
    };
    this.outputId = config.outputId || 'json-output';
    this.outputElement = config.outputElement || document.getElementById(this.outputId);
    this.translator = typeof config.translate === 'function' ? config.translate : null;
    this.notifier = typeof config.onNotify === 'function' ? config.onNotify : null;
    this.onChange = typeof config.onChange === 'function' ? config.onChange : null;
    this.data = [];
    this.nextId = 1;
    this.collapsedIds = new Set();
    this.drag = null;
    this.dragGhost = null;
    this.indicator = null;
    this._pendingFocus = null;

    if (!this.container) {
      console.error('TreeManager: container #' + this.containerId + ' not found');
      return;
    }
    // The SPA router re-runs the page script on every visit. Without this the
    // previous instance keeps its listeners alive and each drop is applied twice.
    if (this.container._treeManager) {
      this.container._treeManager.destroy();
    }
    this.container._treeManager = this;
    this._bindEvents();
  }

  /* ------------------------------------------------------------------ *
   * Events
   * ------------------------------------------------------------------ */

  _bindEvents() {
    this._handlers = {
      dragstart: (e) => this._onDragStart(e),
      dragover: (e) => this._onDragOver(e),
      dragleave: (e) => this._onDragLeave(e),
      drop: (e) => this._onDrop(e),
      dragend: () => this._endDrag(),
      keydown: (e) => {
        if (e.key === 'Escape' && this.drag) {
          this._endDrag();
        }
      },
      scroll: () => {
        // Cached row rectangles are viewport relative, so scrolling voids them.
        if (this.drag) {
          this.drag.rows = null;
        }
      }
    };
    this.container.addEventListener('dragstart', this._handlers.dragstart);
    this.container.addEventListener('dragover', this._handlers.dragover);
    this.container.addEventListener('dragleave', this._handlers.dragleave);
    this.container.addEventListener('drop', this._handlers.drop);
    // dragend must be global: after a drop the source row no longer exists.
    document.addEventListener('dragend', this._handlers.dragend);
    document.addEventListener('keydown', this._handlers.keydown);
    window.addEventListener('scroll', this._handlers.scroll, true);
  }

  destroy() {
    this._endDrag();
    if (this._handlers) {
      this.container.removeEventListener('dragstart', this._handlers.dragstart);
      this.container.removeEventListener('dragover', this._handlers.dragover);
      this.container.removeEventListener('dragleave', this._handlers.dragleave);
      this.container.removeEventListener('drop', this._handlers.drop);
      document.removeEventListener('dragend', this._handlers.dragend);
      document.removeEventListener('keydown', this._handlers.keydown);
      window.removeEventListener('scroll', this._handlers.scroll, true);
      this._handlers = null;
    }
    if (this.indicator && this.indicator.parentNode) {
      this.indicator.parentNode.removeChild(this.indicator);
    }
    this.indicator = null;
    if (this.container && this.container._treeManager === this) {
      delete this.container._treeManager;
    }
  }

  /* ------------------------------------------------------------------ *
   * Drag & drop
   * ------------------------------------------------------------------ */

  _onDragStart(e) {
    if (!e.target.closest) {
      return;
    }
    // Selecting text inside the name field raises its own dragstart; that is
    // a text drag, not a node drag.
    if (e.target.closest('input, textarea, button')) {
      return;
    }
    const content = e.target.closest('.tree-content');
    if (!content || content.draggable !== true || !this.container.contains(content)) {
      return;
    }
    const item = content.parentElement;
    const node = this._findNodeById(this._toId(item.dataset.id));
    if (!node) {
      return;
    }
    this.drag = {
      node: node,
      item: item,
      content: content,
      target: null,
      position: null,
      valid: false,
      rows: null,
      expandId: null,
      expandTimer: null
    };
    this.scrollParent = this._scrollParent();
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', String(node.id));
    } catch (err) {
      // Some browsers refuse custom payloads; the drag itself still works.
    }
    const rect = content.getBoundingClientRect();
    const ghost = this._createDragGhost(content, rect.width);
    document.body.appendChild(ghost);
    this.dragGhost = ghost;
    e.dataTransfer.setDragImage(ghost, e.clientX - rect.left, e.clientY - rect.top);
    // Styling the source in the same tick cancels the drag in Chrome.
    setTimeout(() => {
      if (this.drag) {
        this.drag.content.classList.add('tree-dragging');
        this.drag.item.classList.add('tree-drop-blocked');
      }
    }, 0);
  }

  _onDragOver(e) {
    if (!this.drag) {
      return;
    }
    this._autoScroll(e.clientY);
    const row = this._hitTest(e.clientY);
    if (!row) {
      this._clearTarget();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'none';
      }
      return;
    }
    const position = this._resolvePosition(row, e.clientY);
    const check = this._canDrop(this.drag.node, row.node, position);
    this.drag.target = row.node;
    this.drag.position = position;
    this.drag.valid = check.ok;
    this._queueAutoExpand(row, position);
    this._showIndicator(row, position, check.ok, check.ok ? this._intentLabel(row.node, position) : check.reason);
    if (check.ok) {
      // Only a prevented dragover accepts a drop, so an invalid target simply
      // never fires one and the cursor stays "no drop".
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'move';
      }
    } else if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'none';
    }
  }

  _onDragLeave(e) {
    if (!this.drag) {
      return;
    }
    const to = e.relatedTarget;
    if (!to || !this.container.contains(to)) {
      this._clearTarget();
    }
  }

  _onDrop(e) {
    if (!this.drag) {
      return;
    }
    // Re-resolve from the drop coordinates so a stale dragover result can
    // never be applied; keep the last valid one if the pointer left the rows.
    const row = this._hitTest(e.clientY);
    if (row) {
      const position = this._resolvePosition(row, e.clientY);
      if (this._canDrop(this.drag.node, row.node, position).ok) {
        this.drag.target = row.node;
        this.drag.position = position;
        this.drag.valid = true;
      }
    }
    if (!this.drag.target || !this.drag.valid) {
      this._endDrag();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const nodeId = this.drag.node.id;
    const targetId = this.drag.target.id;
    const position = this.drag.position;
    this._endDrag();
    this.moveNode(nodeId, targetId, position);
  }

  _clearTarget() {
    this._cancelAutoExpand();
    this._hideIndicator();
    if (this.drag) {
      this.drag.target = null;
      this.drag.position = null;
      this.drag.valid = false;
    }
  }

  _endDrag() {
    this._cancelAutoExpand();
    this._hideIndicator();
    if (this.dragGhost && this.dragGhost.parentNode) {
      this.dragGhost.parentNode.removeChild(this.dragGhost);
    }
    this.dragGhost = null;
    if (this.container) {
      this.container.querySelectorAll('.tree-dragging').forEach(el => el.classList.remove('tree-dragging'));
      this.container.querySelectorAll('.tree-drop-blocked').forEach(el => el.classList.remove('tree-drop-blocked'));
    }
    this.drag = null;
  }

  /**
   * Every visible row that can be aimed at, with its cached rectangle.
   * The dragged node and its whole subtree are left out - they can never
   * receive the drop, and including them makes the pointer flicker.
   */
  _rows() {
    if (this.drag.rows) {
      return this.drag.rows;
    }
    const excluded = new Set([this.drag.node.id]);
    const collect = (node) => {
      (node.children || []).forEach(child => {
        excluded.add(child.id);
        collect(child);
      });
    };
    collect(this.drag.node);

    const rows = [];
    this.container.querySelectorAll('.tree-item').forEach(item => {
      const id = this._toId(item.dataset.id);
      if (excluded.has(id)) {
        return;
      }
      const content = item.firstElementChild;
      if (!content || !content.classList.contains('tree-content')) {
        return;
      }
      const rect = content.getBoundingClientRect();
      // A collapsed ancestor hides the row; a zero sized rect means invisible.
      if (rect.height === 0 || rect.width === 0) {
        return;
      }
      const node = this._findNodeById(id);
      if (node) {
        rows.push({node: node, item: item, content: content, rect: rect});
      }
    });
    this.drag.rows = rows;
    return rows;
  }

  /**
   * Pointer to row. Falls back to the nearest row when the pointer sits in a
   * gap between rows, so a drop is never aimed at nothing.
   */
  _hitTest(y) {
    const rows = this._rows();
    if (!rows.length) {
      return null;
    }
    for (const row of rows) {
      if (y >= row.rect.top && y <= row.rect.bottom) {
        return row;
      }
    }
    let nearest = null;
    let bestDistance = Infinity;
    for (const row of rows) {
      const distance = y < row.rect.top ? row.rect.top - y : y - row.rect.bottom;
      if (distance < bestDistance) {
        bestDistance = distance;
        nearest = row;
      }
    }
    return bestDistance <= 40 ? nearest : null;
  }

  /**
   * Which of the three intents the pointer expresses:
   * the top and bottom thirds reorder, the middle third nests.
   */
  _resolvePosition(row, y) {
    const height = row.rect.height || 1;
    const offset = (y - row.rect.top) / height;
    if (offset < 0.3) {
      return 'before';
    }
    if (offset > 0.7) {
      return 'after';
    }
    return 'inside';
  }

  /**
   * @returns {{ok: boolean, reason: string}}
   */
  _canDrop(node, target, position) {
    if (!node || !target || node.id === target.id) {
      return {ok: false, reason: ''};
    }
    if (this._isDescendantOf(target.id, node)) {
      return {ok: false, reason: this._t('Cannot move an item inside itself')};
    }
    if (this.limitedDepth) {
      const baseLevel = position === 'inside' ? target.level + 1 : target.level;
      // The dragged node brings its own subtree along.
      if (baseLevel + this._subtreeDepth(node) - 1 > this.maxDepth) {
        return {ok: false, reason: this._t('Limited to {max} levels', {max: this.maxDepth})};
      }
    }
    return {ok: true, reason: ''};
  }

  _intentLabel(target, position) {
    const name = target.name || this._t('Untitled');
    if (position === 'before') {
      return this._t('Insert above') + ' “' + name + '”';
    }
    if (position === 'after') {
      return this._t('Insert below') + ' “' + name + '”';
    }
    return this._t('Move into') + ' “' + name + '”';
  }

  _ensureIndicator() {
    if (this.indicator) {
      return this.indicator;
    }
    const el = document.createElement('div');
    el.className = 'tree-drop-indicator';
    el.style.display = 'none';
    this.indicatorLabel = document.createElement('span');
    this.indicatorLabel.className = 'tree-drop-indicator-label';
    el.appendChild(this.indicatorLabel);
    document.body.appendChild(el);
    this.indicator = el;
    return el;
  }

  /**
   * A line for a reorder, a box around the row for a nest - two clearly
   * different shapes plus a caption spelling out what the drop will do.
   */
  _showIndicator(row, position, valid, message) {
    const el = this._ensureIndicator();
    const rect = row.rect;
    el.className = 'tree-drop-indicator ' +
      (position === 'inside' ? 'is-box' : 'is-line') +
      (valid ? '' : ' is-invalid');
    el.style.display = 'block';
    el.style.left = rect.left + 'px';
    el.style.width = rect.width + 'px';
    if (position === 'inside') {
      el.style.top = rect.top + 'px';
      el.style.height = rect.height + 'px';
    } else {
      el.style.top = (position === 'before' ? rect.top : rect.bottom) + 'px';
      el.style.height = '0px';
    }
    this.indicatorLabel.textContent = message || '';
    this.indicatorLabel.style.display = message ? '' : 'none';
  }

  _hideIndicator() {
    if (this.indicator) {
      this.indicator.style.display = 'none';
    }
  }

  _queueAutoExpand(row, position) {
    const collapsed = position === 'inside' &&
      row.node.children && row.node.children.length > 0 &&
      this.collapsedIds.has(row.node.id);
    if (!collapsed) {
      this._cancelAutoExpand();
      return;
    }
    if (this.drag.expandId === row.node.id) {
      return;
    }
    this._cancelAutoExpand();
    this.drag.expandId = row.node.id;
    this.drag.expandTimer = setTimeout(() => {
      this.collapsedIds.delete(row.node.id);
      row.item.classList.remove('collapsed');
      const btn = row.item.querySelector('.collapse-btn');
      if (btn) {
        btn.innerHTML = '&#9660;';
      }
      if (this.drag) {
        this.drag.rows = null;
        this.drag.expandTimer = null;
      }
    }, 600);
  }

  _cancelAutoExpand() {
    if (this.drag && this.drag.expandTimer) {
      clearTimeout(this.drag.expandTimer);
      this.drag.expandTimer = null;
      this.drag.expandId = null;
    }
  }

  _scrollParent() {
    let node = this.container.parentElement;
    while (node && node !== document.body) {
      const overflow = getComputedStyle(node).overflowY;
      if (/(auto|scroll|overlay)/.test(overflow) && node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  _autoScroll(y) {
    const margin = 70;
    const speed = 14;
    const box = this.scrollParent
      ? this.scrollParent.getBoundingClientRect()
      : {top: 0, bottom: window.innerHeight};
    let delta = 0;
    if (y < box.top + margin) {
      delta = -speed * Math.min(1, (box.top + margin - y) / margin);
    } else if (y > box.bottom - margin) {
      delta = speed * Math.min(1, (y - box.bottom + margin) / margin);
    }
    if (!delta) {
      return;
    }
    if (this.scrollParent) {
      this.scrollParent.scrollTop += delta;
    } else {
      window.scrollBy(0, delta);
    }
    if (this.drag) {
      this.drag.rows = null;
    }
  }

  _createDragGhost(original, width) {
    const ghost = original.cloneNode(true);
    ghost.classList.add('tree-drag-ghost');
    ghost.classList.remove('tree-dragging');
    const actions = ghost.querySelector('.tree-actions-menu');
    if (actions) {
      actions.remove();
    }
    ghost.style.position = 'fixed';
    ghost.style.top = '-1000px';
    ghost.style.left = '0';
    ghost.style.width = width + 'px';
    return ghost;
  }

  /* ------------------------------------------------------------------ *
   * Structure helpers - all of them work off the real nesting, never off
   * a stored parentId, so data that arrives without one still moves.
   * ------------------------------------------------------------------ */

  _toId(value) {
    const id = parseInt(value, 10);
    return Number.isInteger(id) ? id : null;
  }

  _findNodeById(id, nodes = this.data) {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const found = this._findNodeById(id, node.children);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  _findParentNode(nodeId, nodes = this.data) {
    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        if (node.children.some(child => child.id === nodeId)) {
          return node;
        }
        const found = this._findParentNode(nodeId, node.children);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  /** The array a node lives in: its parent's children, or the roots. */
  _siblingsOf(nodeId) {
    const parent = this._findParentNode(nodeId);
    return parent ? parent.children : this.data;
  }

  _detach(nodeId) {
    const siblings = this._siblingsOf(nodeId);
    const index = siblings.findIndex(node => node.id === nodeId);
    return index === -1 ? null : siblings.splice(index, 1)[0];
  }

  _isDescendantOf(nodeId, ancestor) {
    if (!ancestor.children || ancestor.children.length === 0) {
      return false;
    }
    for (const child of ancestor.children) {
      if (child.id === nodeId || this._isDescendantOf(nodeId, child)) {
        return true;
      }
    }
    return false;
  }

  /** How many levels the node occupies, itself included (a leaf is 1). */
  _subtreeDepth(node) {
    if (!node.children || node.children.length === 0) {
      return 1;
    }
    let deepest = 0;
    for (const child of node.children) {
      const depth = this._subtreeDepth(child);
      if (depth > deepest) {
        deepest = depth;
      }
    }
    return deepest + 1;
  }

  /** Re-derives level and parentId from the actual nesting. */
  _reindex(nodes = this.data, level = 1, parentId = null) {
    nodes.forEach(node => {
      node.level = level;
      node.parentId = parentId;
      if (!Array.isArray(node.children)) {
        node.children = [];
      }
      this._reindex(node.children, level + 1, node.id);
    });
  }

  _commit(focus) {
    this._pendingFocus = focus || null;
    this._reindex();
    this._render();
    this._emit();
  }

  /* ------------------------------------------------------------------ *
   * Public API
   * ------------------------------------------------------------------ */

  addRootNode(name) {
    const node = {
      id: this.nextId++,
      name: name || this._t('New item'),
      level: 1,
      parentId: null,
      children: []
    };
    this.data.push(node);
    this._commit({id: node.id, action: 'name'});
    return node;
  }

  addChildNode(parentId, name) {
    const parent = this._findNodeById(parentId);
    if (!parent) {
      return null;
    }
    if (this.limitedDepth && parent.level >= this.maxDepth) {
      this._notify(this._t('Limited to {max} levels', {max: this.maxDepth}), 'warning');
      return null;
    }
    const node = {
      id: this.nextId++,
      name: name || this._t('New item'),
      level: parent.level + 1,
      parentId: parent.id,
      children: []
    };
    if (!parent.children) {
      parent.children = [];
    }
    parent.children.push(node);
    this.collapsedIds.delete(parent.id);
    this._commit({id: node.id, action: 'name'});
    return node;
  }

  deleteNode(nodeId) {
    if (!this._detach(nodeId)) {
      return false;
    }
    this.collapsedIds.delete(nodeId);
    this._commit();
    return true;
  }

  /**
   * @param {number} nodeId   node being moved
   * @param {number} targetId node it is dropped onto
   * @param {string} position 'before' | 'after' (reorder as a sibling of the
   *                          target) or 'inside' (become the target's last child)
   */
  moveNode(nodeId, targetId, position) {
    const node = this._findNodeById(nodeId);
    const target = this._findNodeById(targetId);
    const check = this._canDrop(node, target, position);
    if (!check.ok) {
      if (check.reason) {
        this._notify(check.reason, 'warning');
      }
      return false;
    }
    this._detach(nodeId);
    if (position === 'inside') {
      if (!target.children) {
        target.children = [];
      }
      target.children.push(node);
      this.collapsedIds.delete(target.id);
    } else {
      // Read the siblings after detaching: removing the node shifts the index.
      const siblings = this._siblingsOf(target.id);
      const index = siblings.findIndex(item => item.id === target.id);
      siblings.splice(position === 'before' ? index : index + 1, 0, node);
    }
    // No focus to restore: the pointer did the work, and stealing focus into
    // the name field would make the next keystroke rename the node.
    this._commit();
    return true;
  }

  moveNodeUp(nodeId) {
    const siblings = this._siblingsOf(nodeId);
    const index = siblings.findIndex(node => node.id === nodeId);
    if (index <= 0) {
      return false;
    }
    siblings.splice(index - 1, 0, siblings.splice(index, 1)[0]);
    this._commit({id: nodeId, action: 'up'});
    return true;
  }

  moveNodeDown(nodeId) {
    const siblings = this._siblingsOf(nodeId);
    const index = siblings.findIndex(node => node.id === nodeId);
    if (index === -1 || index >= siblings.length - 1) {
      return false;
    }
    siblings.splice(index + 1, 0, siblings.splice(index, 1)[0]);
    this._commit({id: nodeId, action: 'down'});
    return true;
  }

  /** Outdent: become the next sibling of the current parent. */
  moveNodeLeft(nodeId) {
    const parent = this._findParentNode(nodeId);
    if (!parent) {
      return false;
    }
    const node = this._detach(nodeId);
    if (!node) {
      return false;
    }
    const uncles = this._siblingsOf(parent.id);
    const parentIndex = uncles.findIndex(item => item.id === parent.id);
    uncles.splice(parentIndex + 1, 0, node);
    this._commit({id: nodeId, action: 'left'});
    return true;
  }

  /** Indent: become the last child of the previous sibling. */
  moveNodeRight(nodeId) {
    const siblings = this._siblingsOf(nodeId);
    const index = siblings.findIndex(node => node.id === nodeId);
    if (index <= 0) {
      return false;
    }
    const node = siblings[index];
    const newParent = siblings[index - 1];
    if (this.limitedDepth && newParent.level + this._subtreeDepth(node) > this.maxDepth) {
      this._notify(this._t('Limited to {max} levels', {max: this.maxDepth}), 'warning');
      return false;
    }
    siblings.splice(index, 1);
    if (!newParent.children) {
      newParent.children = [];
    }
    newParent.children.push(node);
    this.collapsedIds.delete(newParent.id);
    this._commit({id: nodeId, action: 'right'});
    return true;
  }

  collapseAll() {
    const collect = (nodes) => {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          this.collapsedIds.add(node.id);
          collect(node.children);
        }
      });
    };
    collect(this.data);
    this._render();
  }

  expandAll() {
    this.collapsedIds.clear();
    this._render();
  }

  getTreeData() {
    this._reindex();
    return JSON.parse(JSON.stringify(this.data));
  }

  /**
   * Accepts both the shape this class produces and raw tree_nodes rows from
   * the API (string ids, parent_id instead of parentId, a level column that
   * may have drifted). Everything is coerced and levels are recomputed.
   */
  setTreeData(data) {
    this.data = this._normalize(Array.isArray(data) ? data : []);
    let maxId = 0;
    const scan = (nodes) => {
      nodes.forEach(node => {
        if (node.id > maxId) {
          maxId = node.id;
        }
        scan(node.children);
      });
    };
    scan(this.data);
    this.nextId = maxId + 1;
    this.collapsedIds.clear();
    this._commit();
  }

  _normalize(nodes) {
    const used = new Set();
    const scan = (list) => {
      list.forEach(raw => {
        const id = this._toId(raw && raw.id);
        if (id !== null && id > 0) {
          used.add(id);
        }
        if (raw && Array.isArray(raw.children)) {
          scan(raw.children);
        }
      });
    };
    scan(nodes);
    let next = 1;
    used.forEach(id => {
      if (id >= next) {
        next = id + 1;
      }
    });
    const taken = new Set();
    const walk = (list) => list.map(raw => {
      let id = this._toId(raw && raw.id);
      if (id === null || id <= 0 || taken.has(id)) {
        id = next++;
      }
      taken.add(id);
      return {
        id: id,
        name: raw && raw.name !== undefined && raw.name !== null ? String(raw.name) : '',
        level: 1,
        parentId: null,
        children: raw && Array.isArray(raw.children) ? walk(raw.children) : []
      };
    });
    return walk(nodes);
  }

  generateInitialStructure() {
    this.setTreeData([
      {
        name: this._t('Room'),
        children: [
          {name: this._t('Floor 1'), children: [{name: this._t('Zone A')}]},
          {name: this._t('Floor 2')}
        ]
      },
      {name: this._t('Corridor')}
    ]);
  }

  updateConfig(config = {}) {
    if (config.limitedDepth !== undefined) {
      this.limitedDepth = config.limitedDepth;
    }
    if (config.maxDepth !== undefined) {
      this.maxDepth = config.maxDepth;
    }
    if (config.levelNames !== undefined) {
      this.levelNames = config.levelNames;
    }
    this._render();
  }

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */

  _render() {
    if (!this.container) {
      return;
    }
    this.container.innerHTML = '';
    this.data.forEach((node, index) => {
      this.container.appendChild(this._createTreeItemElement(node, this.data, index));
    });
    const focus = this._pendingFocus;
    this._pendingFocus = null;
    if (focus) {
      this._restoreFocus(focus);
    }
  }

  /** Keeps the keyboard/mouse on the node that just moved. */
  _restoreFocus(focus) {
    const item = this.container.querySelector('.tree-item[data-id="' + focus.id + '"]');
    if (!item) {
      return;
    }
    const selector = focus.action === 'name'
      ? '.tree-name'
      : '.tree-action-btn[data-action="' + focus.action + '"]';
    const el = item.querySelector(selector);
    if (el && !el.disabled) {
      el.focus();
      if (focus.action === 'name' && typeof el.select === 'function') {
        el.select();
      }
    }
  }

  _createTreeItemElement(node, siblings, index) {
    const hasChildren = node.children && node.children.length > 0;
    const collapsed = hasChildren && this.collapsedIds.has(node.id);

    const item = document.createElement('div');
    item.className = 'tree-item' + (collapsed ? ' collapsed' : '');
    item.dataset.id = node.id;

    const content = document.createElement('div');
    content.className = 'tree-content level-' + node.level;
    content.draggable = true;

    const handle = document.createElement('span');
    handle.className = 'tree-drag-handle';
    handle.title = this._t('Drag to move');
    handle.innerHTML = '<svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true">' +
      '<circle cx="2.5" cy="3" r="1.4"/><circle cx="7.5" cy="3" r="1.4"/>' +
      '<circle cx="2.5" cy="8" r="1.4"/><circle cx="7.5" cy="8" r="1.4"/>' +
      '<circle cx="2.5" cy="13" r="1.4"/><circle cx="7.5" cy="13" r="1.4"/></svg>';
    content.appendChild(handle);

    const collapseBtn = document.createElement('span');
    collapseBtn.className = 'collapse-btn';
    collapseBtn.innerHTML = hasChildren ? (collapsed ? '&#9658;' : '&#9660;') : '&nbsp;';
    if (hasChildren) {
      collapseBtn.addEventListener('click', () => {
        if (this.collapsedIds.has(node.id)) {
          this.collapsedIds.delete(node.id);
        } else {
          this.collapsedIds.add(node.id);
        }
        const isCollapsed = this.collapsedIds.has(node.id);
        item.classList.toggle('collapsed', isCollapsed);
        collapseBtn.innerHTML = isCollapsed ? '&#9658;' : '&#9660;';
      });
    }
    content.appendChild(collapseBtn);

    const label = document.createElement('div');
    label.className = 'tree-label';
    const levelTag = document.createElement('span');
    levelTag.className = 'tree-level-tag';
    levelTag.textContent = this._t(this.levelNames[node.level] || 'Level {level}', {level: node.level});
    label.appendChild(levelTag);

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'tree-name';
    nameInput.value = node.name;
    nameInput.addEventListener('input', () => {
      node.name = nameInput.value;
      this._emit();
    });
    nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        nameInput.blur();
      }
    });
    label.appendChild(nameInput);
    content.appendChild(label);

    const actionsMenu = document.createElement('div');
    actionsMenu.className = 'tree-actions-menu';
    const canIndent = index > 0 &&
      (!this.limitedDepth || siblings[index - 1].level + this._subtreeDepth(node) <= this.maxDepth);
    [
      {
        action: 'up',
        title: 'Move up',
        enabled: index > 0,
        path: 'M12 19V5M12 5L5 12M12 5L19 12',
        run: () => this.moveNodeUp(node.id)
      },
      {
        action: 'down',
        title: 'Move down',
        enabled: index < siblings.length - 1,
        path: 'M12 5V19M12 19L5 12M12 19L19 12',
        run: () => this.moveNodeDown(node.id)
      },
      {
        action: 'left',
        title: 'Move out (up one level)',
        enabled: node.level > 1,
        path: 'M19 12H5M5 12L12 5M5 12L12 19',
        run: () => this.moveNodeLeft(node.id)
      },
      {
        action: 'right',
        title: 'Move in (down one level)',
        enabled: canIndent,
        path: 'M5 12H19M19 12L12 5M19 12L12 19',
        run: () => this.moveNodeRight(node.id)
      },
      {
        action: 'add',
        title: 'Add child item',
        enabled: !this.limitedDepth || node.level < this.maxDepth,
        path: 'M12 4V20M4 12H20',
        run: () => this.addChildNode(node.id)
      },
      {
        action: 'delete',
        title: 'Delete this item',
        enabled: true,
        className: 'tree-action-danger',
        path: 'M19 7L18.1327 19.1425C18.0579 20.1891 17.187 21 16.1378 21H7.86224C6.81296 21 5.94208 20.1891 5.86732 19.1425L5 7M10 11V17M14 11V17M15 7V4C15 3.44772 14.5523 3 14 3H10C9.44772 3 9 3.44772 9 4V7M4 7H20',
        run: () => {
          if (confirm(this._t('Are you sure you want to delete this item and all its children?'))) {
            this.deleteNode(node.id);
          }
        }
      }
    ].forEach(spec => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tree-action-btn' + (spec.className ? ' ' + spec.className : '');
      btn.dataset.action = spec.action;
      btn.title = this._t(spec.title);
      btn.disabled = !spec.enabled;
      btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path d="' + spec.path + '" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      btn.addEventListener('click', spec.run);
      actionsMenu.appendChild(btn);
    });
    content.appendChild(actionsMenu);

    // Typing in the name field or pressing a button must not start a drag:
    // dragging inside a draggable parent hijacks text selection and clicks.
    const suspendDrag = () => {
      content.draggable = false;
      document.addEventListener('pointerup', () => {
        content.draggable = true;
      }, {once: true});
    };
    nameInput.addEventListener('pointerdown', suspendDrag);
    actionsMenu.addEventListener('pointerdown', suspendDrag);
    nameInput.addEventListener('blur', () => {
      content.draggable = true;
    });

    item.appendChild(content);

    if (hasChildren) {
      const children = document.createElement('div');
      children.className = 'tree-children';
      node.children.forEach((child, childIndex) => {
        children.appendChild(this._createTreeItemElement(child, node.children, childIndex));
      });
      item.appendChild(children);
    }
    return item;
  }

  _emit() {
    if (this.outputElement) {
      const json = JSON.stringify(this.data, null, 2);
      if ('value' in this.outputElement) {
        this.outputElement.value = json;
      } else {
        this.outputElement.textContent = json;
      }
    }
    if (this.onChange) {
      this.onChange(this.data);
    }
  }

  _t(text, params) {
    let out = text;
    if (this.translator) {
      out = this.translator(text, params) || text;
    } else if (window.I18nManager && typeof window.I18nManager.translate === 'function') {
      out = window.I18nManager.translate(text, params || {}) || text;
    }
    if (params) {
      Object.keys(params).forEach(key => {
        out = out.split('{' + key + '}').join(params[key]);
      });
    }
    return out;
  }

  _notify(message, type = 'info') {
    if (this.notifier) {
      this.notifier(message, type);
      return;
    }
    if (window.NotificationManager && typeof window.NotificationManager[type] === 'function') {
      window.NotificationManager[type](message);
      return;
    }
    console.warn('TreeManager: ' + message);
  }
}

window.TreeManager = TreeManager;
